import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Notice, traceButtonClass } from '@traceio-org/auth-ui';
import { useHub } from '../app/context';
import { DevSection } from '../app/dev-identity';
import { SigningInPage, rememberSigningInProduct } from '../app/signing-in';
import { SIGNUP_PRODUCT, signupConnection } from '../app/signup-options';
import { HubFrame, PageHeading } from '../app/ui';
import { safeHubPath } from '../security/hub-path';
import { readReturnTo } from '../security/return-path';

/**
 * The hub path to come back to after sign-up: returnTo, kept only when it is one of the hub's own return routes
 * (safeHubPath, for example /start), so the page never becomes an open redirect.
 */
export function signupReturnPath(search: string): string | undefined {
  return safeHubPath(readReturnTo(search));
}

type SignupState = { kind: 'starting' } | { kind: 'unavailable'; devIdentity: boolean } | { kind: 'failed' };

/**
 * /signup?returnTo=<hub path> (spec 2026-10-18 piece 3). Opens the identity provider's sign-up screens on the
 * connection platform-api names (loginOptions.signup), with the work email rule enforced there. With a session
 * already open, sign-up is skipped and the person goes straight to the return path. Nothing links here except the
 * hub's own /start page (piece 5), so nothing changes for anyone until the self-serve switch is on.
 */
export function SignupPage() {
  const deps = useHub();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = signupReturnPath(location.search);
  const [state, setState] = useState<SignupState>({ kind: 'starting' });
  const [attempt, setAttempt] = useState(0);
  // One start per attempt: StrictMode's second effect run must not leave for the identity provider twice.
  const startedAttempt = useRef(-1);

  const leave = useCallback(() => {
    navigate(returnTo ?? `/login/${SIGNUP_PRODUCT}`, { replace: true });
  }, [navigate, returnTo]);

  useEffect(() => {
    if (startedAttempt.current === attempt) return;
    startedAttempt.current = attempt;
    void (async () => {
      const session = await deps.sessions.restore();
      if (session) {
        leave();
        return;
      }
      let connection: string | null;
      let devIdentity: boolean;
      try {
        const options = await deps.client.loginOptions(SIGNUP_PRODUCT);
        devIdentity = options.developmentIdentity.enabled;
        connection = signupConnection(options);
      } catch (error) {
        console.warn('account-hub: could not load the sign-up options', error);
        setState({ kind: 'failed' });
        return;
      }
      const auth0 = deps.sessions.auth0;
      if (!auth0 || !connection) {
        setState({ kind: 'unavailable', devIdentity });
        return;
      }
      rememberSigningInProduct(SIGNUP_PRODUCT);
      try {
        // Settles only when the page did not leave; while it leaves, the steady signing-in screen stays.
        await auth0.beginSignIn({
          productKey: SIGNUP_PRODUCT,
          screenHint: 'signup',
          connection,
          ...(returnTo ? { hubPath: returnTo } : {}),
        });
      } catch (error) {
        console.warn('account-hub: could not start sign-up', error);
        setState({ kind: 'failed' });
      }
    })();
  }, [attempt, deps, leave, returnTo]);

  // Local development without Auth0: a development identity stands in for the new account.
  const afterDevSignIn = useCallback(async () => {
    leave();
    return null;
  }, [leave]);

  if (state.kind === 'unavailable') {
    return (
      <HubFrame productKey={SIGNUP_PRODUCT}>
        <PageHeading
          title="Sign-up is not available"
          lead="New Trace Enterprise accounts cannot be created here yet. If you already have an account, sign in."
        />
        <Link className={traceButtonClass('primary', { block: true })} to={`/login/${SIGNUP_PRODUCT}`}>
          Go to sign-in
        </Link>
        <DevSection enabled={state.devIdentity} afterSignIn={afterDevSignIn} />
      </HubFrame>
    );
  }

  if (state.kind === 'failed') {
    return (
      <HubFrame productKey={SIGNUP_PRODUCT}>
        <PageHeading title="We could not start sign-up" lead="Check your connection, then try again." />
        <Notice tone="error" live="assertive" title="The sign-in service did not answer." />
        <button
          type="button"
          className={traceButtonClass('primary', { block: true })}
          onClick={() => {
            setState({ kind: 'starting' });
            setAttempt((n) => n + 1);
          }}
        >
          Try again
        </button>
      </HubFrame>
    );
  }

  return <SigningInPage productKey={SIGNUP_PRODUCT} />;
}
